import React, { useState, useMemo } from "react";
import {
  ArrowRight, ArrowLeft, Calendar, Search,
  CheckCircle2, ChevronRight, ChevronDown, Mail, Sparkles, Clock,
  Globe2, ShieldCheck, Check, BadgeCheck, PlayCircle, ExternalLink,
  GraduationCap, Banknote, CalendarClock, Hourglass, Plus, Trash2, Brain
} from "lucide-react";
import { getConceitoDoDia, getReferenciaTexto } from "./data/conceitos";

/* ---------------------------------------------------------------
   TOKENS
   ink   #1B2A4A  (navy institucional, textos fortes, headers)
   parch #F6F1E7  (pergaminho, fundo)
   brass #B8862E  (acento quente, destaque)
   sage  #5E7A68  (secundário calmo)
   line  #DCD3BD  (bordas sobre pergaminho)
----------------------------------------------------------------*/

const INSTITUTO_HOME_URL = "https://institutointernacionaldepsicanalise.com/";

const PSICANALISTAS = [
  {
    id: 1,
    nome: "Helena Marquetti",
    abordagem: "Lacaniana",
    idiomas: ["Português", "Espanhol"],
    valor: 220,
    bio: "Atende adultos e atravessamentos de luto e angústia, com escuta orientada pela clínica do significante.",
    horarios: ["Seg 09:00", "Seg 14:00", "Qua 10:00", "Qui 16:00"],
  },
  {
    id: 2,
    nome: "Theo Anastácio",
    abordagem: "Freudiana clássica",
    idiomas: ["Português", "Inglês"],
    valor: 180,
    bio: "Foco em sintomas obsessivos e estruturação do desejo. Atendimento online para todo o Brasil e diáspora.",
    horarios: ["Ter 08:00", "Ter 18:00", "Sex 09:00"],
  },
  {
    id: 3,
    nome: "Camila Outeiro",
    abordagem: "Winnicottiana",
    idiomas: ["Português", "Francês"],
    valor: 200,
    bio: "Trabalha com adolescentes e questões de constituição do self, ambiente facilitador e holding.",
    horarios: ["Seg 16:00", "Qua 15:00", "Qua 17:00", "Sáb 10:00"],
  },
];

const LIVROS = [
  { titulo: "O Treino Invisível", selo: "Lançamento", desc: "Vol. 1 de A Conquista de Si — ascese cínico-estoica e escuta psicanalítica." },
  { titulo: "Enciclopédia das Síndromes", selo: "238 verbetes", desc: "Obra de referência em quatro idiomas, por WILLIAM MARCOS." },
];

const PLANOS_PSICANALISTA = [
  {
    id: "essencial",
    nome: "Essencial",
    preco: 0,
    badge: null,
    beneficios: [
      "Perfil básico, sem destaque na busca",
      "Até 3 horários disponíveis por semana",
      "Recebe solicitações por e-mail",
    ],
  },
  {
    id: "associado",
    nome: "Associado Pleno",
    preco: 89,
    badge: "Mais escolhido",
    beneficios: [
      "Perfil completo e destacado na busca",
      "Agenda ilimitada de horários",
      "Selo de associado verificado",
      "Aparece na Agenda Psicanalítica como referência",
      "Suporte prioritário do Instituto",
    ],
  },
];

function Logo({ small }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: small ? 26 : 34,
          height: small ? 26 : 34,
          borderRadius: 7,
          background: "#1B2A4A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <span style={{ color: "#B8862E", fontFamily: "Georgia, serif", fontWeight: 700, fontSize: small ? 14 : 18 }}>
          I
        </span>
      </div>
      <div style={{ lineHeight: 1.05 }}>
        <div style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: small ? 13 : 16, color: "#1B2A4A" }}>
          Instituto Internacional
        </div>
        <div style={{ fontSize: small ? 9 : 10.5, color: "#5E7A68", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          de Psicanálise
        </div>
      </div>
    </div>
  );
}

function TopBar({ user, onNav, screen }) {
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        background: "#F6F1E7",
        borderBottom: "1px solid #DCD3BD",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
        <a href={INSTITUTO_HOME_URL} style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>
          <Logo small />
        </a>
        <a
          href={INSTITUTO_HOME_URL}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            color: "#6B6858",
            fontSize: 12,
            fontFamily: "'Public Sans', sans-serif",
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          <ArrowLeft size={13} /> Voltar ao site do Instituto
        </a>
      </div>
      {user && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#1B2A4A",
              color: "#F6F1E7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {user.nome.charAt(0)}
          </div>
        </div>
      )}
    </div>
  );
}

function BottomNav({ screen, onNav }) {
  const items = [
    { id: "hub", label: "Início", icon: Sparkles },
    { id: "agenda", label: "A Hora do Inconsciente", icon: Calendar },
    { id: "teste-qi", label: "Teste Cognitivo", icon: Brain },
  ];
  return (
    <div
      style={{
        position: "sticky",
        bottom: 0,
        background: "#F6F1E7",
        borderTop: "1px solid #DCD3BD",
        display: "flex",
        padding: "6px 4px 10px",
        zIndex: 20,
      }}
    >
      {items.map((it) => {
        const active = screen === it.id || (it.id !== "hub" && screen.startsWith(it.id));
        return (
          <button
            key={it.id}
            onClick={() => onNav(it.id)}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              padding: "6px 2px",
              cursor: "pointer",
              color: active ? "#1B2A4A" : "#8A8270",
            }}
          >
            <it.icon size={20} strokeWidth={active ? 2.4 : 1.8} />
            <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 500, textAlign: "center", lineHeight: 1.1 }}>
              {it.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Landing({ onStart, onCadastro }) {
  return (
    <div style={{ padding: "28px 20px 40px", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
        <a href={INSTITUTO_HOME_URL} style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
          <Logo />
        </a>
        <a
          href={INSTITUTO_HOME_URL}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            color: "#6B6858",
            fontSize: 12,
            fontFamily: "'Public Sans', sans-serif",
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          <ArrowLeft size={13} /> Voltar ao site do Instituto
        </a>
      </div>
      <div style={{ marginTop: 28 }}>
        <span
          style={{
            fontSize: 11.5,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#B8862E",
            fontWeight: 700,
          }}
        >
          Espaço Aberto — acesso livre, sem cadastro
        </span>
        <h1
          style={{
            fontFamily: "Georgia, serif",
            fontSize: 32,
            lineHeight: 1.18,
            color: "#1B2A4A",
            margin: "10px 0 14px",
          }}
        >
          A escuta tem hora.
          <br />A sua, também.
        </h1>
        <p style={{ fontSize: 15, color: "#46443B", lineHeight: 1.6, marginBottom: 12 }}>
          Existem coisas que a mente só entrega quando ninguém está cobrando pressa. Este é um
          espaço sem formulário, sem fila de espera e sem promessa de diagnóstico — só duas portas
          abertas para quem quer olhar um pouco mais para dentro.
        </p>
        <p style={{ fontSize: 15, color: "#46443B", lineHeight: 1.6, marginBottom: 12 }}>
          <b>A Hora do Inconsciente</b> é um hábito diário: um conceito da psicanálise por dia,
          explicado sem jargão, com um caso real para ilustrar e um quiz para testar se ficou.
          Amanhã vem outro. Depois, outro. Um percurso, não uma aula.
        </p>
        <p style={{ fontSize: 15, color: "#46443B", lineHeight: 1.6, marginBottom: 12 }}>
          O <b>Teste Cognitivo</b> é uma pausa de alguns minutos para medir — com honestidade sobre
          os limites do que qualquer teste curto pode dizer — como sua mente resolve padrões,
          números, palavras, memória e velocidade hoje.
        </p>
        <p style={{ fontSize: 15, color: "#46443B", lineHeight: 1.6 }}>
          Nenhum dos dois pede cadastro. Nenhum dos dois guarda nada depois que você fecha a aba.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 26 }}>
        {[
          { icon: Calendar, t: "A Hora do Inconsciente", d: "Um conceito por dia, estudo de caso e quiz." },
          { icon: Brain, t: "Teste Cognitivo", d: "Aferição cognitiva online, gratuita e rápida." },
        ].map((f, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
              background: "#FFFFFF",
              border: "1px solid #DCD3BD",
              borderRadius: 12,
              padding: "14px 14px",
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: "#1B2A4A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <f.icon size={18} color="#F6F1E7" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14.5, color: "#1B2A4A" }}>{f.t}</div>
              <div style={{ fontSize: 13, color: "#6B6858" }}>{f.d}</div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onStart}
        style={{
          marginTop: 28,
          width: "100%",
          background: "#1B2A4A",
          color: "#F6F1E7",
          border: "none",
          borderRadius: 12,
          padding: "15px",
          fontSize: 15,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        Começar agora <ArrowRight size={17} />
      </button>
      <p style={{ fontSize: 11.5, color: "#8A8270", textAlign: "center", marginTop: 10 }}>
        Gratuito. Sem cadastro, sem login.
      </p>
    </div>
  );
}

function Cadastro({ onDone }) {
  const [step, setStep] = useState("google");
  const [loading, setLoading] = useState(false);

  const fakeUser = { nome: "Renata Bicalho", email: "renata.bicalho@gmail.com" };

  function continuarGoogle() {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep("confirma");
    }, 900);
  }

  return (
    <div style={{ padding: "28px 20px 40px", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <a href={INSTITUTO_HOME_URL} style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
          <Logo small />
        </a>
        <a
          href={INSTITUTO_HOME_URL}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            color: "#6B6858",
            fontSize: 12,
            fontFamily: "'Public Sans', sans-serif",
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          <ArrowLeft size={13} /> Voltar ao site do Instituto
        </a>
      </div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1B2A4A", margin: "22px 0 6px" }}>
        Seu acesso institucional
      </h2>

      {step === "google" && (
        <div>
          <p style={{ fontSize: 14, color: "#46443B", marginBottom: 22, lineHeight: 1.55 }}>
            Entre com sua conta Google. Esse mesmo acesso libera a Agenda, o InstitutoAtende e as
            correspondências por e-mail do Instituto — sem cadastros repetidos.
          </p>
          <button
            onClick={continuarGoogle}
            disabled={loading}
            style={{
              width: "100%",
              background: "#FFFFFF",
              border: "1px solid #DCD3BD",
              borderRadius: 12,
              padding: "14px",
              fontSize: 14.5,
              fontWeight: 700,
              color: "#1B2A4A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              cursor: "pointer",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.55-1.83.86-3.06.86-2.36 0-4.36-1.6-5.07-3.74H.86v2.34A9 9 0 0 0 9 18z" />
              <path fill="#FBBC05" d="M3.93 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.15.29-1.68V4.98H.86A9 9 0 0 0 0 9c0 1.45.35 2.82.86 4.02l3.07-2.34z" />
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.6 8.6 0 0 0 9 0 9 9 0 0 0 .86 4.98l3.07 2.34C4.64 5.18 6.64 3.58 9 3.58z" />
            </svg>
            {loading ? "Conectando…" : "Continuar com Google"}
          </button>
          <p style={{ fontSize: 11.5, color: "#8A8270", marginTop: 12, lineHeight: 1.5 }}>
            Simulação de login Google para este protótipo — no sistema final, este botão abre o
            consentimento real do Google (OAuth) e cria sua sessão de forma segura.
          </p>
        </div>
      )}

      {step === "confirma" && (
        <div>
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #DCD3BD",
              borderRadius: 12,
              padding: 16,
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 18,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#1B2A4A",
                color: "#F6F1E7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              }}
            >
              R
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14.5, color: "#1B2A4A" }}>{fakeUser.nome}</div>
              <div style={{ fontSize: 12.5, color: "#6B6858" }}>{fakeUser.email}</div>
            </div>
            <CheckCircle2 size={20} color="#5E7A68" style={{ marginLeft: "auto" }} />
          </div>

          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, color: "#46443B", marginBottom: 14 }}>
            <input type="checkbox" defaultChecked style={{ marginTop: 3 }} />
            Quero receber as correspondências do Instituto (novidades editoriais, canal no YouTube e
            agenda de estudo) neste e-mail.
          </label>

          <button
            onClick={() => onDone(fakeUser)}
            style={{
              width: "100%",
              background: "#1B2A4A",
              color: "#F6F1E7",
              border: "none",
              borderRadius: 12,
              padding: "15px",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            Concluir e entrar <ArrowRight size={17} />
          </button>
        </div>
      )}
    </div>
  );
}

function Hub({ user, onNav }) {
  const primeiroNome = user?.nome?.split(" ")[0];
  const conceitoHoje = getConceitoDoDia();

  return (
    <div style={{ padding: "20px 20px 12px", maxWidth: 480, margin: "0 auto" }}>
      <p style={{ fontSize: 13, color: "#6B6858" }}>{primeiroNome ? "Bem-vinda," : "Bem-vindo(a) ao"}</p>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1B2A4A", margin: "2px 0 16px" }}>
        {primeiroNome || "Instituto Internacional de Psicanálise"}
      </h2>

      <button
        onClick={() => onNav("psi-intro")}
        style={{
          width: "100%",
          textAlign: "left",
          background: "#B8862E",
          border: "none",
          borderRadius: 14,
          padding: 16,
          marginBottom: 18,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <GraduationCap size={22} color="#1B2A4A" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14.5, color: "#1B2A4A" }}>É psicanalista?</div>
          <div style={{ fontSize: 12, color: "#4A3A18" }}>Associe-se ao InstitutoAtende e receba pacientes</div>
        </div>
        <ChevronRight size={18} color="#1B2A4A" />
      </button>

      <div style={{ fontSize: 11, color: "#8A8270", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", margin: "4px 0 8px" }}>
        Espaço Aberto
      </div>

      <button
        onClick={() => onNav("agenda")}
        style={{
          width: "100%",
          textAlign: "left",
          background: "#1B2A4A",
          border: "none",
          borderRadius: 14,
          padding: 18,
          marginBottom: 14,
          cursor: "pointer",
          color: "#F6F1E7",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 11, color: "#B8862E", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Percurso diário
            </div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 18, marginTop: 4 }}>A Hora do Inconsciente</div>
            <div style={{ fontSize: 12.5, color: "#C9C2AD", marginTop: 4 }}>
              Hoje: conceito de "{conceitoHoje?.titulo}"
            </div>
          </div>
          <ChevronRight size={20} />
        </div>
      </button>

      <button
        onClick={() => onNav("teste-qi")}
        style={{
          width: "100%",
          textAlign: "left",
          background: "#FFFFFF",
          border: "1px solid #DCD3BD",
          borderRadius: 14,
          padding: 18,
          marginBottom: 14,
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 11, color: "#B8862E", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Aferição cognitiva
            </div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 18, marginTop: 4, color: "#1B2A4A" }}>
              Teste Cognitivo
            </div>
            <div style={{ fontSize: 12.5, color: "#6B6858", marginTop: 4 }}>
              Teste de QI online gratuito, leva poucos minutos
            </div>
          </div>
          <ChevronRight size={20} color="#1B2A4A" />
        </div>
      </button>

      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #DCD3BD",
          borderRadius: 14,
          padding: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Mail size={16} color="#1B2A4A" />
          <span style={{ fontWeight: 700, fontSize: 13.5, color: "#1B2A4A" }}>Novidades do Instituto</span>
        </div>
        {LIVROS.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: i > 0 ? "1px solid #EFE9D9" : "none" }}>
            <div style={{ fontSize: 13, flex: 1 }}>
              <span style={{ fontWeight: 700, color: "#1B2A4A" }}>{l.titulo}</span>
              <span style={{ color: "#8A8270" }}> — {l.desc}</span>
            </div>
          </div>
        ))}
        <button
          onClick={() => window.open("https://www.youtube.com/@institutointernacionalpsicanal", "_blank")}
          style={{
            marginTop: 8,
            background: "none",
            border: "none",
            color: "#B8862E",
            fontSize: 12.5,
            fontWeight: 700,
            padding: 0,
            display: "flex",
            alignItems: "center",
            gap: 4,
            cursor: "pointer",
          }}
        >
          Ver no canal do YouTube <ExternalLink size={13} />
        </button>
      </div>
    </div>
  );
}

function AgendaPsicanalitica({ onNav }) {
  const [respondido, setRespondido] = useState(null);
  const [casoAberto, setCasoAberto] = useState(false);
  const c = useMemo(() => getConceitoDoDia(), []);
  const referenciaTexto = useMemo(() => getReferenciaTexto(c), [c]);
  const hoje = new Date();
  const dias = ["D", "S", "T", "Q", "Q", "S", "S"];
  const diaAtual = hoje.getDate();

  return (
    <div style={{ padding: "18px 20px 30px", maxWidth: 480, margin: "0 auto" }}>
      <button onClick={() => onNav("hub")} style={{ background: "none", border: "none", padding: 0, color: "#6B6858", fontSize: 13, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", marginBottom: 14 }}>
        <ArrowLeft size={15} /> Início
      </button>

      <span style={{ fontSize: 11, color: "#B8862E", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        A Hora do Inconsciente
      </span>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1B2A4A", margin: "4px 0 16px" }}>
        Percurso diário de psicanálise
      </h2>

      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 13.5, color: "#5E7A68", fontWeight: 700, lineHeight: 1.5, margin: "0 0 8px" }}>
          Um conceito por dia. Nunca o mesmo dois dias seguidos.
        </p>
        <p style={{ fontSize: 12.5, color: "#8A8270", lineHeight: 1.6, margin: "0 0 8px" }}>
          Psicanálise não se aprende de uma vez — se aprende em doses, devagar, do jeito que a
          própria escuta clínica funciona. É essa a lógica aqui: hoje você encontra um conceito, um
          caso que o ilustra e uma pergunta para testar se ele ficou. Amanhã será outro, diferente,
          tirado de um autor diferente. Freud, Lacan, Klein, Winnicott, Jung, Ferenczi — o percurso
          alterna entre eles sem avisar qual vem a seguir.
        </p>
        <p style={{ fontSize: 12.5, color: "#8A8270", lineHeight: 1.6, margin: 0 }}>
          Não dá para adiantar o de amanhã, nem voltar ao de ontem. É um convite a voltar, não um
          arquivo para vasculhar de uma vez.
        </p>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[diaAtual - 2, diaAtual - 1, diaAtual, diaAtual + 1, diaAtual + 2].map((d, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              textAlign: "center",
              padding: "8px 0",
              borderRadius: 10,
              background: i === 2 ? "#1B2A4A" : "#FFFFFF",
              border: i === 2 ? "none" : "1px solid #DCD3BD",
            }}
          >
            <div style={{ fontSize: 10, color: i === 2 ? "#C9C2AD" : "#8A8270" }}>{dias[(d - 1 + 7) % 7]}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: i === 2 ? "#F6F1E7" : "#1B2A4A" }}>{d}</div>
            {i === 2 && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#B8862E", margin: "3px auto 0" }} />}
          </div>
        ))}
      </div>

      <div style={{ background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 14, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#5E7A68", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>
          Conceito do dia
        </div>
        <h3 style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1B2A4A", margin: "0 0 10px" }}>{c.titulo}</h3>
        <p style={{ fontSize: 14, color: "#3A3830", lineHeight: 1.6, margin: "0 0 12px" }}>{c.definicao}</p>
        <div style={{ background: "#F6F1E7", borderRadius: 10, padding: 12, fontSize: 13.5, color: "#46443B", lineHeight: 1.55 }}>
          <b style={{ color: "#1B2A4A" }}>Exemplo cotidiano: </b>
          {c.exemplo}
        </div>
        {referenciaTexto && (
          <div style={{ fontSize: 11, color: "#8A8270", marginTop: 12, paddingTop: 10, borderTop: "1px solid #EFE9D9", lineHeight: 1.4 }}>
            Referência: {referenciaTexto}
          </div>
        )}
      </div>

      <div style={{ background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 14, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#5E7A68", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>
          Estudo de caso
        </div>
        <p style={{ fontSize: 14, color: "#3A3830", lineHeight: 1.6, margin: 0 }}>{c.casoResumo}</p>

        {casoAberto && (
          <div style={{ marginTop: 14, borderTop: "1px solid #EFE9D9", paddingTop: 14 }}>
            {c.casoCompleto.map((par, i) => (
              <p key={i} style={{ fontSize: 13.5, color: "#46443B", lineHeight: 1.65, margin: "0 0 12px" }}>
                {par}
              </p>
            ))}
          </div>
        )}

        <button
          onClick={() => setCasoAberto(!casoAberto)}
          style={{
            marginTop: 8,
            background: "none",
            border: "none",
            color: "#B8862E",
            fontSize: 12.5,
            fontWeight: 700,
            padding: 0,
            display: "flex",
            alignItems: "center",
            gap: 4,
            cursor: "pointer",
          }}
        >
          {casoAberto ? "Recolher caso" : "Ler caso completo"}
          <ChevronDown size={14} style={{ transform: casoAberto ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </button>
      </div>

      <div style={{ background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 14, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#5E7A68", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 10 }}>
          Quiz rápido
        </div>
        <p style={{ fontSize: 14.5, color: "#1B2A4A", fontWeight: 700, marginBottom: 10 }}>{c.quiz.pergunta}</p>
        {c.quiz.opcoes.map((op, i) => {
          const escolhida = respondido === i;
          const ehCorreta = i === c.quiz.correta;
          let bg = "#FFFFFF", border = "#DCD3BD", color = "#3A3830";
          if (respondido !== null) {
            if (ehCorreta) { bg = "#EAF1EC"; border = "#5E7A68"; color = "#2F4538"; }
            else if (escolhida) { bg = "#FBEAEA"; border = "#C25B5B"; color = "#7A2E2E"; }
          }
          return (
            <button
              key={i}
              onClick={() => respondido === null && setRespondido(i)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "11px 12px",
                borderRadius: 10,
                border: `1px solid ${border}`,
                background: bg,
                color,
                fontSize: 13.5,
                marginBottom: 8,
                cursor: respondido === null ? "pointer" : "default",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {op}
              {respondido !== null && ehCorreta && <Check size={16} />}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => window.open("https://www.youtube.com/@institutointernacionalpsicanal", "_blank")}
        style={{
          width: "100%",
          background: "#1B2A4A",
          border: "none",
          borderRadius: 14,
          padding: 18,
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "rgba(246,241,231,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PlayCircle size={24} color="#F6F1E7" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#B8862E", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            YouTube
          </div>
          <div style={{ fontSize: 15, color: "#F6F1E7", fontWeight: 700, marginTop: 2 }}>
            Conheça o canal do Instituto no YouTube
          </div>
        </div>
        <ExternalLink size={18} color="#F6F1E7" />
      </button>
    </div>
  );
}

function TesteCognitivo({ onNav }) {
  return (
    <div style={{ padding: "18px 20px 30px", maxWidth: 480, margin: "0 auto" }}>
      <button onClick={() => onNav("hub")} style={{ background: "none", border: "none", padding: 0, color: "#6B6858", fontSize: 13, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", marginBottom: 14 }}>
        <ArrowLeft size={15} /> Início
      </button>

      <span style={{ fontSize: 11, color: "#B8862E", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        Aferição cognitiva
      </span>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1B2A4A", margin: "4px 0 16px" }}>
        Teste Cognitivo
      </h2>

      <div style={{ border: "1px solid #DCD3BD", borderRadius: 14, overflow: "hidden", background: "#FFFFFF" }}>
        <iframe
          src={`${process.env.PUBLIC_URL}/teste-cognitivo.html`}
          title="Teste Cognitivo"
          style={{ width: "100%", height: "78vh", border: "none", display: "block" }}
        />
      </div>
    </div>
  );
}

function Atende({ onNav, onAbrirPerfil }) {
  const [busca, setBusca] = useState("");
  const filtrados = useMemo(
    () =>
      PSICANALISTAS.filter(
        (p) =>
          p.nome.toLowerCase().includes(busca.toLowerCase()) ||
          p.abordagem.toLowerCase().includes(busca.toLowerCase())
      ),
    [busca]
  );

  return (
    <div style={{ padding: "18px 20px 30px", maxWidth: 480, margin: "0 auto" }}>
      <button onClick={() => onNav("hub")} style={{ background: "none", border: "none", padding: 0, color: "#6B6858", fontSize: 13, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", marginBottom: 14 }}>
        <ArrowLeft size={15} /> Início
      </button>

      <span style={{ fontSize: 11, color: "#5E7A68", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        Agendamento
      </span>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1B2A4A", margin: "4px 0 16px" }}>
        InstitutoAtende
      </h2>

      <div style={{ position: "relative", marginBottom: 18 }}>
        <Search size={16} color="#8A8270" style={{ position: "absolute", left: 12, top: 12 }} />
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome ou abordagem"
          style={{
            width: "100%",
            padding: "11px 12px 11px 36px",
            borderRadius: 10,
            border: "1px solid #DCD3BD",
            fontSize: 14,
            background: "#FFFFFF",
            boxSizing: "border-box",
          }}
        />
      </div>

      <p style={{ fontSize: 12, color: "#8A8270", marginBottom: 12 }}>
        {filtrados.length} psicanalistas associados encontrados
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtrados.map((p) => (
          <button
            key={p.id}
            onClick={() => onAbrirPerfil(p)}
            style={{
              textAlign: "left",
              background: "#FFFFFF",
              border: "1px solid #DCD3BD",
              borderRadius: 14,
              padding: 16,
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", gap: 12 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "#EFE9D9",
                  color: "#1B2A4A",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  flexShrink: 0,
                  fontSize: 15,
                }}
              >
                {p.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ fontWeight: 700, fontSize: 14.5, color: "#1B2A4A" }}>{p.nome}</div>
                  <BadgeCheck size={16} color="#5E7A68" />
                </div>
                <div style={{ fontSize: 12.5, color: "#5E7A68", fontWeight: 600 }}>{p.abordagem}</div>
                <div style={{ fontSize: 12.5, color: "#8A8270", marginTop: 4 }}>
                  {p.idiomas.join(" · ")} · R$ {p.valor}/sessão
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Perfil({ p, onVoltar, onAgendar }) {
  return (
    <div style={{ padding: "18px 20px 30px", maxWidth: 480, margin: "0 auto" }}>
      <button onClick={onVoltar} style={{ background: "none", border: "none", padding: 0, color: "#6B6858", fontSize: 13, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", marginBottom: 14 }}>
        <ArrowLeft size={15} /> Voltar à busca
      </button>

      <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 18 }}>
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: "#EFE9D9",
            color: "#1B2A4A",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 20,
          }}
        >
          {p.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 19, color: "#1B2A4A", margin: 0 }}>{p.nome}</h2>
            <BadgeCheck size={16} color="#5E7A68" />
          </div>
          <div style={{ fontSize: 13, color: "#5E7A68", fontWeight: 600 }}>{p.abordagem}</div>
        </div>
      </div>

      <p style={{ fontSize: 14, color: "#3A3830", lineHeight: 1.6, marginBottom: 16 }}>{p.bio}</p>

      <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
        <div style={{ flex: 1, background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 10, padding: 10, textAlign: "center" }}>
          <Globe2 size={15} color="#1B2A4A" style={{ marginBottom: 4 }} />
          <div style={{ fontSize: 11.5, color: "#6B6858" }}>{p.idiomas.join(" · ")}</div>
        </div>
        <div style={{ flex: 1, background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 10, padding: 10, textAlign: "center" }}>
          <ShieldCheck size={15} color="#1B2A4A" style={{ marginBottom: 4 }} />
          <div style={{ fontSize: 11.5, color: "#6B6858" }}>Associado verificado</div>
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#5E7A68", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 10 }}>
        Horários disponíveis
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
        {p.horarios.map((h, i) => (
          <button
            key={i}
            onClick={() => onAgendar(h)}
            style={{
              padding: "11px 8px",
              borderRadius: 10,
              border: "1px solid #DCD3BD",
              background: "#FFFFFF",
              fontSize: 13,
              fontWeight: 600,
              color: "#1B2A4A",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <Clock size={13} /> {h}
          </button>
        ))}
      </div>

      <div style={{ background: "#F6F1E7", borderRadius: 10, padding: 12, fontSize: 12.5, color: "#6B6858", lineHeight: 1.5 }}>
        Sessão de R$ {p.valor} acordada diretamente com {p.nome.split(" ")[0]}. O link de
        videochamada (Zoom, Meet ou outro de preferência do psicanalista) é enviado após a confirmação.
      </div>
    </div>
  );
}

function Confirmacao({ p, horario, onFinalizar }) {
  return (
    <div style={{ padding: "60px 24px", maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#EAF1EC",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 18px",
        }}
      >
        <CheckCircle2 size={32} color="#5E7A68" />
      </div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", marginBottom: 8 }}>
        Sessão solicitada
      </h2>
      <p style={{ fontSize: 14, color: "#46443B", lineHeight: 1.6, marginBottom: 24 }}>
        Você solicitou <b>{horario}</b> com <b>{p.nome}</b>. Você receberá a confirmação e o link da
        sessão por e-mail.
      </p>
      <button
        onClick={onFinalizar}
        style={{
          background: "#1B2A4A",
          color: "#F6F1E7",
          border: "none",
          borderRadius: 12,
          padding: "13px 28px",
          fontSize: 14.5,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        Voltar ao início
      </button>
    </div>
  );
}

function PsiIntro({ onNav }) {
  return (
    <div style={{ padding: "18px 20px 30px", maxWidth: 480, margin: "0 auto" }}>
      <button onClick={() => onNav("hub")} style={{ background: "none", border: "none", padding: 0, color: "#6B6858", fontSize: 13, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", marginBottom: 18 }}>
        <ArrowLeft size={15} /> Início
      </button>

      <span style={{ fontSize: 11, color: "#B8862E", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        Para psicanalistas
      </span>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 23, color: "#1B2A4A", margin: "4px 0 14px" }}>
        Associe-se ao InstitutoAtende
      </h2>
      <p style={{ fontSize: 14.5, color: "#46443B", lineHeight: 1.6, marginBottom: 22 }}>
        Tenha presença profissional dentro do ecossistema do Instituto Internacional de Psicanálise.
        Pacientes encontram seu perfil, agendam diretamente com você, e a sessão acontece como você preferir
        — fora da plataforma, no canal que já usa.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 26 }}>
        {[
          { icon: BadgeCheck, t: "Curadoria, não vitrine aberta", d: "Perfis passam por verificação antes de ficarem públicos." },
          { icon: Banknote, t: "Assinatura fixa, sem comissão", d: "Você combina o valor da sessão direto com o paciente." },
          { icon: CalendarClock, t: "Você controla sua agenda", d: "Defina seus próprios horários, sem obrigação de exclusividade." },
        ].map((f, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 12, padding: 14 }}>
            <f.icon size={20} color="#1B2A4A" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1B2A4A" }}>{f.t}</div>
              <div style={{ fontSize: 12.5, color: "#6B6858" }}>{f.d}</div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => onNav("psi-candidatura")}
        style={{
          width: "100%",
          background: "#1B2A4A",
          color: "#F6F1E7",
          border: "none",
          borderRadius: 12,
          padding: "15px",
          fontSize: 15,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        Iniciar minha candidatura <ArrowRight size={17} />
      </button>
    </div>
  );
}

const EMAIL_ASSOCIACAO = "contato@institutointernacionaldepsicanalise.com";

function PsiCandidatura({ onEnviar }) {
  const [form, setForm] = useState({
    nomeCompleto: "",
    endereco: "",
    whatsapp: "",
    especialidade: "",
    mensagem: "",
  });

  const completo =
    form.nomeCompleto.trim() &&
    form.endereco.trim() &&
    form.whatsapp.trim() &&
    form.especialidade.trim() &&
    form.mensagem.trim();

  function set(campo, valor) {
    setForm({ ...form, [campo]: valor });
  }

  function enviar() {
    const assunto = encodeURIComponent(`Pedido de associação — ${form.nomeCompleto}`);
    const corpo = encodeURIComponent(
      `Nome completo: ${form.nomeCompleto}\n` +
      `Endereço completo: ${form.endereco}\n` +
      `WhatsApp: ${form.whatsapp}\n` +
      `Especialidade: ${form.especialidade}\n\n` +
      `Mensagem:\n${form.mensagem}`
    );
    window.open(`mailto:${EMAIL_ASSOCIACAO}?subject=${assunto}&body=${corpo}`);
    onEnviar();
  }

  const campos = [
    { campo: "nomeCompleto", label: "Nome completo", placeholder: "Seu nome completo" },
    { campo: "endereco", label: "Endereço completo", placeholder: "Rua, número, bairro, cidade, UF" },
    { campo: "whatsapp", label: "Número de WhatsApp com DDD", placeholder: "(11) 91234-5678" },
    { campo: "especialidade", label: "Especialidade", placeholder: "Ex: Lacaniana, Freudiana, Winnicottiana" },
  ];

  return (
    <div style={{ padding: "18px 20px 40px", maxWidth: 480, margin: "0 auto" }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", margin: "0 0 6px" }}>
        Pedido de associação
      </h2>
      <p style={{ fontSize: 13.5, color: "#6B6858", marginBottom: 20 }}>
        Preencha seus dados e nos conte um pouco sobre seu interesse. O Instituto entrará em
        contato pelo WhatsApp ou e-mail informado.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {campos.map((c) => (
          <div key={c.campo}>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: "#1B2A4A", display: "block", marginBottom: 5 }}>
              {c.label}
            </label>
            <input
              value={form[c.campo]}
              onChange={(e) => set(c.campo, e.target.value)}
              placeholder={c.placeholder}
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #DCD3BD", fontSize: 14, boxSizing: "border-box" }}
            />
          </div>
        ))}

        <div>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: "#1B2A4A", display: "block", marginBottom: 5 }}>
            Pedido de associação
          </label>
          <textarea
            value={form.mensagem}
            onChange={(e) => set("mensagem", e.target.value)}
            placeholder="Conte um pouco sobre sua formação e por que quer se associar ao Instituto."
            rows={5}
            style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #DCD3BD", fontSize: 14, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
          />
        </div>
      </div>

      <button
        onClick={enviar}
        disabled={!completo}
        style={{
          width: "100%",
          marginTop: 22,
          background: completo ? "#1B2A4A" : "#C9C2AD",
          color: "#F6F1E7",
          border: "none",
          borderRadius: 12,
          padding: "15px",
          fontSize: 15,
          fontWeight: 700,
          cursor: completo ? "pointer" : "not-allowed",
        }}
      >
        Enviar pedido de associação
      </button>
      <p style={{ fontSize: 11, color: "#8A8270", textAlign: "center", marginTop: 10, lineHeight: 1.4 }}>
        Ao enviar este formulário, seu e-mail será automaticamente incluído na newsletter do site.
      </p>
      {!completo && (
        <p style={{ fontSize: 11.5, color: "#8A8270", textAlign: "center", marginTop: 4 }}>
          Preencha todos os campos para continuar.
        </p>
      )}
    </div>
  );
}

function PedidoAssociacaoEnviado({ onVoltar }) {
  return (
    <div style={{ padding: "60px 24px", maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#EAF1EC",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 18px",
        }}
      >
        <CheckCircle2 size={32} color="#5E7A68" />
      </div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", marginBottom: 8 }}>
        Pedido enviado
      </h2>
      <p style={{ fontSize: 14, color: "#46443B", lineHeight: 1.6, marginBottom: 24 }}>
        Seu pedido de associação foi preparado para envio por e-mail — confirme o envio no seu
        aplicativo de e-mail, caso não tenha aberto automaticamente. O Instituto entrará em contato
        pelo WhatsApp ou e-mail informado.
      </p>
      <button
        onClick={onVoltar}
        style={{
          background: "#1B2A4A",
          color: "#F6F1E7",
          border: "none",
          borderRadius: 12,
          padding: "13px 28px",
          fontSize: 14.5,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        Voltar ao início
      </button>
    </div>
  );
}

function PsiAnalise({ onSimularAprovacao }) {
  const [verChecklist, setVerChecklist] = useState(false);
  const itens = [
    { t: "Diploma/certificado em PDF recebido", ok: true },
    { t: "Instituição formadora identificável", ok: true },
    { t: "Vínculo associativo coerente com a formação declarada", ok: true },
    { t: "Apresentação demonstra domínio teórico mínimo", ok: true },
    { t: "CPF válido para emissão fiscal", ok: true },
  ];

  return (
    <div style={{ padding: "40px 24px 50px", maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#FAEEDA",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 18px",
        }}
      >
        <Hourglass size={30} color="#854F0B" />
      </div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", marginBottom: 8 }}>
        Candidatura em análise
      </h2>
      <p style={{ fontSize: 14, color: "#46443B", lineHeight: 1.6, marginBottom: 20 }}>
        O Instituto verifica suas informações de formação antes de liberar seu perfil. Isso costuma
        levar até 2 dias úteis. Você será avisada por e-mail.
      </p>

      <button
        onClick={() => setVerChecklist(!verChecklist)}
        style={{ background: "none", border: "none", color: "#6B6858", fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginBottom: 16, display: "inline-flex", alignItems: "center", gap: 4 }}
      >
        (Protótipo) ver o que o avaliador do Instituto vê
        <ChevronDown size={13} style={{ transform: verChecklist ? "rotate(180deg)" : "none" }} />
      </button>

      {verChecklist && (
        <div style={{ background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 14, padding: 16, textAlign: "left", marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "#5E7A68", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 10 }}>
            Checklist de verificação
          </div>
          {itens.map((it, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8, fontSize: 13, color: "#3A3830" }}>
              <CheckCircle2 size={15} color="#5E7A68" style={{ flexShrink: 0, marginTop: 1 }} />
              {it.t}
            </div>
          ))}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #EFE9D9", fontSize: 11.5, color: "#8A8270" }}>
            Verificação por amostragem e diligência razoável — não substitui conselho profissional,
            pois psicanálise não possui um órgão regulador único no Brasil.
          </div>
        </div>
      )}

      <button
        onClick={onSimularAprovacao}
        style={{
          background: "none",
          color: "#B8862E",
          border: "1px dashed #B8862E",
          borderRadius: 12,
          padding: "12px 22px",
          fontSize: 13.5,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        (Protótipo) simular aprovação
      </button>
    </div>
  );
}

function PsiPlanos({ onEscolher }) {
  return (
    <div style={{ padding: "18px 20px 40px", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <CheckCircle2 size={16} color="#5E7A68" />
        <span style={{ fontSize: 13, color: "#2F4538", fontWeight: 700 }}>Perfil aprovado</span>
      </div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", margin: "4px 0 18px" }}>
        Escolha como aparecer no InstitutoAtende
      </h2>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {PLANOS_PSICANALISTA.map((plano) => (
          <div
            key={plano.id}
            style={{
              background: "#FFFFFF",
              border: plano.badge ? "2px solid #1B2A4A" : "1px solid #DCD3BD",
              borderRadius: 14,
              padding: 18,
              position: "relative",
            }}
          >
            {plano.badge && (
              <div style={{ position: "absolute", top: -11, left: 16, background: "#1B2A4A", color: "#F6F1E7", fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>
                {plano.badge}
              </div>
            )}
            <div style={{ fontWeight: 700, fontSize: 16, color: "#1B2A4A", marginTop: plano.badge ? 6 : 0 }}>{plano.nome}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4, margin: "6px 0 14px" }}>
              <span style={{ fontSize: 26, fontWeight: 700, color: "#1B2A4A" }}>
                {plano.preco === 0 ? "Grátis" : `R$ ${plano.preco}`}
              </span>
              {plano.preco > 0 && <span style={{ fontSize: 12.5, color: "#8A8270" }}>/mês</span>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {plano.beneficios.map((b, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: "#3A3830" }}>
                  <Check size={15} color="#5E7A68" style={{ flexShrink: 0, marginTop: 1 }} />
                  {b}
                </div>
              ))}
            </div>
            <button
              onClick={() => onEscolher(plano)}
              style={{
                width: "100%",
                background: plano.badge ? "#1B2A4A" : "#FFFFFF",
                color: plano.badge ? "#F6F1E7" : "#1B2A4A",
                border: plano.badge ? "none" : "1px solid #1B2A4A",
                borderRadius: 10,
                padding: "12px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {plano.preco === 0 ? "Continuar grátis" : "Assinar agora"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const CUPONS_VALIDOS = {
  FUNDADOR: { desconto: 100, descricao: "Acesso fundador — sem custo" },
};

function PsiPagamento({ plano, onConfirmar, onCupomFundador }) {
  const [forma, setForma] = useState("cartao");
  const [cupom, setCupom] = useState("");
  const [cupomAplicado, setCupomAplicado] = useState(null);
  const [erroCupom, setErroCupom] = useState(null);
  const precoAnualComDesconto = plano.preco * 10;
  const precoAnualSemDesconto = plano.preco * 12;

  function aplicarCupom() {
    const c = CUPONS_VALIDOS[cupom.trim().toUpperCase()];
    if (c) {
      setCupomAplicado({ codigo: cupom.trim().toUpperCase(), ...c });
      setErroCupom(null);
    } else {
      setErroCupom("Cupom inválido ou expirado.");
      setCupomAplicado(null);
    }
  }

  if (cupomAplicado?.desconto === 100) {
    return (
      <div style={{ padding: "60px 24px", maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#EAF1EC", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
          <BadgeCheck size={32} color="#5E7A68" />
        </div>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", marginBottom: 8 }}>
          Cupom {cupomAplicado.codigo} aplicado
        </h2>
        <p style={{ fontSize: 14, color: "#46443B", lineHeight: 1.6, marginBottom: 24 }}>
          {cupomAplicado.descricao}. Sem cobrança e sem etapa de conferência — acesso liberado
          imediatamente como associado fundador.
        </p>
        <button
          onClick={onCupomFundador}
          style={{ background: "#1B2A4A", color: "#F6F1E7", border: "none", borderRadius: 12, padding: "13px 28px", fontSize: 14.5, fontWeight: 700, cursor: "pointer" }}
        >
          Acessar painel
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "18px 20px 40px", maxWidth: 480, margin: "0 auto" }}>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", margin: "0 0 18px" }}>
        Pagamento da assinatura
      </h2>

      <div style={{ marginBottom: 18 }}>
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "#1B2A4A", display: "block", marginBottom: 5 }}>
          Cupom (opcional)
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={cupom}
            onChange={(e) => setCupom(e.target.value)}
            placeholder="Digite o código do cupom"
            style={{ flex: 1, padding: "11px 12px", borderRadius: 10, border: "1px solid #DCD3BD", fontSize: 14, boxSizing: "border-box", textTransform: "uppercase" }}
          />
          <button
            onClick={aplicarCupom}
            style={{ background: "#FFFFFF", border: "1px solid #1B2A4A", color: "#1B2A4A", borderRadius: 10, padding: "0 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
          >
            Aplicar
          </button>
        </div>
        {erroCupom && <div style={{ fontSize: 11.5, color: "#A32D2D", marginTop: 4 }}>{erroCupom}</div>}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <button
          onClick={() => setForma("cartao")}
          style={{
            flex: 1,
            padding: "11px 8px",
            borderRadius: 10,
            border: forma === "cartao" ? "2px solid #1B2A4A" : "1px solid #DCD3BD",
            background: "#FFFFFF",
            fontSize: 13,
            fontWeight: 700,
            color: "#1B2A4A",
            cursor: "pointer",
          }}
        >
          Cartão · mensal
        </button>
        <button
          onClick={() => setForma("pix")}
          style={{
            flex: 1,
            padding: "11px 8px",
            borderRadius: 10,
            border: forma === "pix" ? "2px solid #1B2A4A" : "1px solid #DCD3BD",
            background: "#FFFFFF",
            fontSize: 13,
            fontWeight: 700,
            color: "#1B2A4A",
            cursor: "pointer",
          }}
        >
          Pix · anual
        </button>
      </div>

      {forma === "cartao" ? (
        <>
          <div style={{ background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 14, padding: 16, marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14.5, color: "#1B2A4A" }}>{plano.nome}</div>
              <div style={{ fontSize: 12.5, color: "#8A8270" }}>Cobrança recorrente mensal</div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1B2A4A" }}>R$ {plano.preco}</div>
          </div>

          <label style={{ fontSize: 12.5, fontWeight: 700, color: "#1B2A4A", display: "block", marginBottom: 5 }}>
            Cartão de crédito
          </label>
          <input placeholder="0000 0000 0000 0000" style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #DCD3BD", fontSize: 14, marginBottom: 10, boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            <input placeholder="MM/AA" style={{ flex: 1, padding: "11px 12px", borderRadius: 10, border: "1px solid #DCD3BD", fontSize: 14, boxSizing: "border-box" }} />
            <input placeholder="CVV" style={{ flex: 1, padding: "11px 12px", borderRadius: 10, border: "1px solid #DCD3BD", fontSize: 14, boxSizing: "border-box" }} />
          </div>

          <p style={{ fontSize: 11.5, color: "#8A8270", marginBottom: 18, lineHeight: 1.5 }}>
            Simulação de checkout neste protótipo — no sistema final, processado via Stripe ou Mercado
            Pago, sem que o Instituto armazene dados do cartão.
          </p>
        </>
      ) : (
        <>
          <div style={{ background: "#FFFFFF", border: "2px solid #1B2A4A", borderRadius: 14, padding: 16, marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14.5, color: "#1B2A4A" }}>{plano.nome} · anual</div>
                <div style={{ fontSize: 12.5, color: "#8A8270" }}>Pagamento único, à vista, por Pix</div>
              </div>
              <div style={{ background: "#EAF1EC", color: "#2F4538", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20 }}>
                2 meses grátis
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 10 }}>
              <span style={{ fontSize: 24, fontWeight: 700, color: "#1B2A4A" }}>
                R$ {precoAnualComDesconto.toLocaleString("pt-BR")}
              </span>
              <span style={{ fontSize: 13, color: "#8A8270", textDecoration: "line-through" }}>
                R$ {precoAnualSemDesconto.toLocaleString("pt-BR")}
              </span>
            </div>
          </div>

          <div style={{ background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 14, padding: 24, marginBottom: 18, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <div style={{ width: 140, height: 140, background: "#F6F1E7", border: "1px dashed #B8862E", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#8A8270", textAlign: "center" }}>
              QR Code Pix
              <br />(simulado)
            </div>
            <div style={{ fontSize: 12.5, color: "#6B6858" }}>Escaneie ou copie o código para pagar</div>
            <button
              style={{ background: "none", border: "1px solid #DCD3BD", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, color: "#1B2A4A", cursor: "pointer" }}
            >
              Copiar código Pix
            </button>
          </div>

          <p style={{ fontSize: 11.5, color: "#8A8270", marginBottom: 18, lineHeight: 1.5 }}>
            Simulação de Pix neste protótipo — no sistema final, a confirmação do pagamento libera o
            acesso automaticamente, sem necessidade de conferência manual. Renovação anual avisada por
            e-mail antes do vencimento.
          </p>
        </>
      )}

      <button
        onClick={onConfirmar}
        style={{
          width: "100%",
          background: "#1B2A4A",
          color: "#F6F1E7",
          border: "none",
          borderRadius: 12,
          padding: "15px",
          fontSize: 15,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        {forma === "pix" ? "Já paguei, enviar para conferência" : "Enviar pagamento para conferência"}
      </button>
      <p style={{ fontSize: 11.5, color: "#8A8270", textAlign: "center", marginTop: 10, lineHeight: 1.5 }}>
        Seu acesso é liberado após o Instituto confirmar manualmente o recebimento do valor —
        normalmente em até 1 dia útil.
      </p>
    </div>
  );
}

function PsiConferenciaPagamento({ onSimularConfirmacao }) {
  return (
    <div style={{ padding: "60px 24px", maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#FAEEDA",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 18px",
        }}
      >
        <Hourglass size={30} color="#854F0B" />
      </div>
      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", marginBottom: 8 }}>
        Pagamento em conferência
      </h2>
      <p style={{ fontSize: 14, color: "#46443B", lineHeight: 1.6, marginBottom: 24 }}>
        Recebemos sua solicitação. O Instituto confirma manualmente o recebimento do valor antes de
        liberar seu acesso como associado. Você será avisada por e-mail assim que confirmado —
        normalmente em até 1 dia útil.
      </p>
      <button
        onClick={onSimularConfirmacao}
        style={{
          background: "none",
          color: "#B8862E",
          border: "1px dashed #B8862E",
          borderRadius: 12,
          padding: "12px 22px",
          fontSize: 13.5,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        (Protótipo) simular confirmação manual
      </button>
    </div>
  );
}

function PsiPainel({ plano, onIrParaHub, onVerRelatorio }) {
  const [horarios, setHorarios] = useState(["Seg 09:00", "Qua 14:00"]);
  const [novo, setNovo] = useState("");
  const ehPleno = plano.id === "associado";

  const [solicitacoes, setSolicitacoes] = useState(
    ehPleno
      ? [
          { id: 1, paciente: "M.S.", horario: "Seg 09:00", status: "pendente" },
          { id: 2, paciente: "R.A.", horario: "Qua 14:00", status: "pendente" },
        ]
      : []
  );

  const limite = plano.id === "essencial" ? 3 : null;
  const noLimite = limite !== null && horarios.length >= limite;

  function adicionar() {
    if (novo.trim() && !noLimite) {
      setHorarios([...horarios, novo.trim()]);
      setNovo("");
    }
  }

  function marcar(id, status) {
    setSolicitacoes(solicitacoes.map((s) => (s.id === id ? { ...s, status } : s)));
  }

  return (
    <div style={{ padding: "18px 20px 40px", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 18 }}>
        <CheckCircle2 size={16} color="#5E7A68" />
        <span style={{ fontSize: 13, color: "#2F4538", fontWeight: 700 }}>
          Assinatura {plano.nome} ativa — você já está visível no InstitutoAtende
        </span>
      </div>

      {ehPleno && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <div style={{ flex: 1, background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
            <BadgeCheck size={15} color="#5E7A68" />
            <span style={{ fontSize: 12, color: "#2F4538", fontWeight: 700 }}>Verificado</span>
          </div>
          <div style={{ flex: 1, background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
            <Clock size={15} color="#B8862E" />
            <span style={{ fontSize: 12, color: "#854F0B", fontWeight: 700 }}>Responde rápido</span>
          </div>
        </div>
      )}

      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", margin: "0 0 6px" }}>
        Sua agenda de atendimento
      </h2>

      {limite !== null && (
        <p style={{ fontSize: 12.5, color: noLimite ? "#A32D2D" : "#8A8270", marginBottom: 16 }}>
          {horarios.length}/{limite} horários usados no plano gratuito.
          {noLimite && " Você atingiu o limite — assine o Associado Pleno para ter agenda ilimitada."}
        </p>
      )}
      {limite === null && (
        <p style={{ fontSize: 12.5, color: "#5E7A68", marginBottom: 16 }}>
          Agenda ilimitada — sem limite de horários no plano {plano.nome}. Listada de forma
          aleatória entre os perfis em destaque, junto aos demais associados Pleno.
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
        {horarios.map((h, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "#1B2A4A", fontWeight: 600 }}>
              <Clock size={14} /> {h}
            </div>
            <button
              onClick={() => setHorarios(horarios.filter((_, idx) => idx !== i))}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
              aria-label="Remover horário"
            >
              <Trash2 size={15} color="#C25B5B" />
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: noLimite ? 8 : 22 }}>
        <input
          value={novo}
          onChange={(e) => setNovo(e.target.value)}
          placeholder="Ex: Sex 10:00"
          disabled={noLimite}
          style={{ flex: 1, padding: "11px 12px", borderRadius: 10, border: "1px solid #DCD3BD", fontSize: 14, boxSizing: "border-box", background: noLimite ? "#EFEAE0" : "#FFFFFF" }}
        />
        <button
          onClick={adicionar}
          disabled={noLimite}
          style={{
            background: noLimite ? "#C9C2AD" : "#1B2A4A",
            color: "#F6F1E7",
            border: "none",
            borderRadius: 10,
            padding: "0 16px",
            cursor: noLimite ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13.5,
            fontWeight: 700,
          }}
        >
          <Plus size={15} /> Adicionar
        </button>
      </div>

      {noLimite && (
        <button
          style={{ width: "100%", background: "none", border: "1px dashed #B8862E", color: "#B8862E", borderRadius: 10, padding: "10px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginBottom: 22 }}
        >
          Assinar Associado Pleno para horários ilimitados
        </button>
      )}

      <div style={{ background: ehPleno ? "#FAEEDA" : "#FFFFFF", border: ehPleno ? "1px solid #EF9F27" : "1px solid #DCD3BD", borderRadius: 14, padding: 16, marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: ehPleno ? "#854F0B" : "#5E7A68", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            Solicitações recebidas
          </div>
          {ehPleno && solicitacoes.some((s) => s.status === "pendente") && (
            <span style={{ background: "#EF9F27", color: "#412402", fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
              {solicitacoes.filter((s) => s.status === "pendente").length} novas
            </span>
          )}
        </div>

        {solicitacoes.length === 0 ? (
          <p style={{ fontSize: 13, color: "#8A8270" }}>
            Nenhuma solicitação ainda. Quando um paciente agendar com você, aparece aqui
            {!ehPleno && " e por e-mail"}.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {solicitacoes.map((s) => (
              <div key={s.id} style={{ background: "#FFFFFF", borderRadius: 10, padding: 12, border: "1px solid #EFE9D9" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: s.status === "pendente" ? 8 : 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#1B2A4A" }}>{s.paciente} · {s.horario}</span>
                  {s.status === "realizado" && <span style={{ fontSize: 11, color: "#2F4538", fontWeight: 700 }}>Realizada</span>}
                  {s.status === "nao_realizado" && <span style={{ fontSize: 11, color: "#A32D2D", fontWeight: 700 }}>Não realizada</span>}
                </div>
                {s.status === "pendente" && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => marcar(s.id, "realizado")}
                      style={{ flex: 1, background: "#EAF1EC", color: "#2F4538", border: "none", borderRadius: 8, padding: "7px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
                    >
                      Houve atendimento
                    </button>
                    <button
                      onClick={() => marcar(s.id, "nao_realizado")}
                      style={{ flex: 1, background: "#FBEAEA", color: "#7A2E2E", border: "none", borderRadius: 8, padding: "7px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
                    >
                      Não houve
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {ehPleno && (
        <button
          onClick={onVerRelatorio}
          style={{ width: "100%", background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 12, padding: "13px", fontSize: 13.5, fontWeight: 700, color: "#1B2A4A", cursor: "pointer", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
        >
          <CalendarClock size={16} /> Ver relatório mensal e estatísticas
        </button>
      )}

      <button
        onClick={onIrParaHub}
        style={{ width: "100%", background: "none", border: "1px solid #DCD3BD", borderRadius: 12, padding: "13px", fontSize: 14, fontWeight: 700, color: "#1B2A4A", cursor: "pointer" }}
      >
        Voltar ao início
      </button>
    </div>
  );
}

function PsiRelatorio({ onVoltar }) {
  return (
    <div style={{ padding: "18px 20px 40px", maxWidth: 480, margin: "0 auto" }}>
      <button onClick={onVoltar} style={{ background: "none", border: "none", padding: 0, color: "#6B6858", fontSize: 13, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", marginBottom: 18 }}>
        <ArrowLeft size={15} /> Voltar ao painel
      </button>

      <h2 style={{ fontFamily: "Georgia, serif", fontSize: 21, color: "#1B2A4A", margin: "0 0 4px" }}>
        Relatório mensal
      </h2>
      <p style={{ fontSize: 12.5, color: "#8A8270", marginBottom: 18 }}>
        Junho de 2026 · dados de exemplo neste protótipo
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
        {[
          { label: "Visualizações do perfil", valor: "142" },
          { label: "Solicitações recebidas", valor: "18" },
          { label: "Sessões realizadas", valor: "11" },
          { label: "Taxa de não comparecimento", valor: "12%" },
        ].map((m, i) => (
          <div key={i} style={{ background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 11, color: "#8A8270" }}>{m.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#1B2A4A", marginTop: 4 }}>{m.valor}</div>
          </div>
        ))}
      </div>

      <div style={{ background: "#FFFFFF", border: "1px solid #DCD3BD", borderRadius: 14, padding: 16, marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: "#5E7A68", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 10 }}>
          Sessões por semana
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 90 }}>
          {[3, 5, 2, 4].map((v, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: "100%", background: "#1B2A4A", borderRadius: 4, height: `${v * 16}px` }} />
              <span style={{ fontSize: 10.5, color: "#8A8270" }}>Sem {i + 1}</span>
            </div>
          ))}
        </div>
      </div>

      <button
        style={{ width: "100%", background: "#1B2A4A", color: "#F6F1E7", border: "none", borderRadius: 12, padding: "14px", fontSize: 14.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
      >
        Baixar relatório em PDF
      </button>
      <p style={{ fontSize: 11, color: "#8A8270", textAlign: "center", marginTop: 8 }}>
        Exportação simulada neste protótipo.
      </p>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState("landing");
  const [user, setUser] = useState(null);
  const [perfilAtivo, setPerfilAtivo] = useState(null);
  const [agendamento, setAgendamento] = useState(null);
  const [planoEscolhido, setPlanoEscolhido] = useState(null);

  function nav(target) {
    setScreen(target);
  }

  const ehFluxoPsi = ["psi-intro", "psi-candidatura", "psi-pedido-enviado", "psi-analise", "psi-planos", "psi-pagamento", "psi-conferencia", "psi-painel", "psi-relatorio"].includes(screen);

  return (
    <div style={{ background: "#F6F1E7", minHeight: "100vh", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {screen !== "landing" && screen !== "cadastro" && <TopBar user={user} onNav={nav} screen={screen} />}

      {screen === "landing" && (
        <Landing onStart={() => setScreen("hub")} onCadastro={() => setScreen("cadastro")} />
      )}

      {screen === "cadastro" && (
        <Cadastro
          onDone={(u) => {
            setUser(u);
            setScreen("hub");
          }}
        />
      )}

      {screen === "hub" && <Hub user={user} onNav={nav} />}

      {screen === "agenda" && <AgendaPsicanalitica onNav={nav} />}

      {screen === "teste-qi" && <TesteCognitivo onNav={nav} />}

      {screen === "atende" && (
        <Atende
          onNav={nav}
          onAbrirPerfil={(p) => {
            setPerfilAtivo(p);
            setScreen("perfil");
          }}
        />
      )}

      {screen === "perfil" && perfilAtivo && (
        <Perfil
          p={perfilAtivo}
          onVoltar={() => setScreen("atende")}
          onAgendar={(h) => {
            setAgendamento(h);
            setScreen("confirmacao");
          }}
        />
      )}

      {screen === "confirmacao" && perfilAtivo && agendamento && (
        <Confirmacao
          p={perfilAtivo}
          horario={agendamento}
          onFinalizar={() => {
            setPerfilAtivo(null);
            setAgendamento(null);
            setScreen("hub");
          }}
        />
      )}

      {screen === "psi-intro" && <PsiIntro onNav={nav} />}

      {screen === "psi-candidatura" && (
        <PsiCandidatura onEnviar={() => setScreen("psi-pedido-enviado")} />
      )}

      {screen === "psi-pedido-enviado" && (
        <PedidoAssociacaoEnviado onVoltar={() => setScreen("hub")} />
      )}

      {screen === "psi-analise" && (
        <PsiAnalise onSimularAprovacao={() => setScreen("psi-planos")} />
      )}

      {screen === "psi-planos" && (
        <PsiPlanos
          onEscolher={(plano) => {
            setPlanoEscolhido(plano);
            if (plano.preco === 0) {
              setScreen("psi-painel");
            } else {
              setScreen("psi-pagamento");
            }
          }}
        />
      )}

      {screen === "psi-pagamento" && planoEscolhido && (
        <PsiPagamento
          plano={planoEscolhido}
          onConfirmar={() => setScreen("psi-conferencia")}
          onCupomFundador={() => setScreen("psi-painel")}
        />
      )}

      {screen === "psi-conferencia" && (
        <PsiConferenciaPagamento onSimularConfirmacao={() => setScreen("psi-painel")} />
      )}

      {screen === "psi-painel" && planoEscolhido && (
        <PsiPainel
          plano={planoEscolhido}
          onIrParaHub={() => setScreen("hub")}
          onVerRelatorio={() => setScreen("psi-relatorio")}
        />
      )}

      {screen === "psi-relatorio" && (
        <PsiRelatorio onVoltar={() => setScreen("psi-painel")} />
      )}

      {!ehFluxoPsi && screen !== "landing" && screen !== "cadastro" && screen !== "perfil" && screen !== "confirmacao" && (
        <BottomNav screen={screen} onNav={nav} />
      )}
    </div>
  );
}
